'use client';

import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useTranslations } from 'next-intl';
import {
    ChangeEvent,
    ClipboardEventHandler,
    DragEventHandler,
    HTMLInputTypeAttribute,
    KeyboardEvent,
    ReactNode,
    useCallback,
    useMemo,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

interface Props {
    name?: string;
    className?: string;
    inputClassName?: string;
    labelSpanClassName?: string;
    label?: string;
    placeholder?: string;
    type?: HTMLInputTypeAttribute;
    value?: string;
    size?: number;
    labelAddon?: ReactNode;
    leftAddon?: ReactNode;
    rightAddon?: ReactNode;
    prefix?: ReactNode;
    prefixClassName?: string;
    suffix?: ReactNode;
    suffixClassName?: string;
    error?: ViolationError;
    successMessage?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    defaultValue?: string;
    form?: string;
    description?: ReactNode;
    noSpaces?: boolean;
    noSymbols?: boolean;
    readonly?: boolean;
    min?: number | string;
    max?: number | string;
    enableErrorOutline?: boolean;
    onPaste?: ClipboardEventHandler<HTMLInputElement>;
    onDrop?: DragEventHandler<HTMLInputElement>;
    autoComplete?: string;
}

export function Input({
    name,
    className,
    inputClassName,
    labelSpanClassName,
    label,
    placeholder,
    type = 'text',
    value,
    size = 1,
    labelAddon,
    leftAddon,
    rightAddon,
    prefix,
    prefixClassName,
    suffix,
    suffixClassName,
    error,
    successMessage,
    onChange,
    defaultValue,
    form,
    description,
    noSpaces = false,
    noSymbols = false,
    readonly = false,
    min,
    max,
    enableErrorOutline = false,
    onPaste,
    onDrop,
    autoComplete,
}: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(error, onChange);

    const displayError = useMemo(() => showError && isViolationMessage(error), [error, showError]);

    const onInput = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            if (noSpaces) {
                e.target.value = e.target.value.replace(/\s/g, '');
            }

            if (noSymbols) {
                e.target.value = e.target.value.replace(/[^\w\s]/g, '');
            }

            if (type === 'number' && min !== undefined && Number(min) >= 0) {
                const value = e.target.value;
                if (value.includes('-') || (value !== '' && Number(value) < Number(min))) {
                    e.target.value = value.replace('-', '');
                    if (e.target.value !== '' && Number(e.target.value) < Number(min)) {
                        e.target.value = String(min);
                    }
                }
            }

            handleChange(e);
        },
        [handleChange, noSpaces, noSymbols, type, min],
    );

    const onKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (type === 'number' && min !== undefined && Number(min) >= 0) {
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                    e.preventDefault();
                }
            }
        },
        [type, min],
    );

    return (
        <div className={twMerge('flex flex-col gap-0.5', className)}>
            {label != null && (
                <label className="mb-1.5 flex items-start gap-1 text-sm">
                    <span className={twMerge('flex-grow text-nowrap', labelSpanClassName)}>{label}</span>
                    {labelAddon != null && <span className="flex-shrink-0">{labelAddon}</span>}
                </label>
            )}
            <div className="flex items-stretch">
                {leftAddon != null && leftAddon}

                <div
                    className={twJoin(
                        'flex grow border-y border-neutral-400 bg-white',
                        'has-[:focus]:border-neutral-500',
                        displayError && enableErrorOutline && 'border-red-500',
                        leftAddon == null && 'rounded-l-md border-l',
                        rightAddon == null && 'rounded-r-md border-r',
                    )}>
                    {prefix != null && (
                        <div
                            className={twMerge(
                                'flex flex-shrink-0 flex-col items-center justify-center px-3',
                                prefixClassName,
                            )}>
                            {prefix}
                        </div>
                    )}

                    <input
                        className={twMerge(
                            'placeholder:text-black/25 min-w-0 flex-grow bg-transparent py-2 outline-none',
                            prefix == null && 'pl-3',
                            suffix == null && 'pr-3',
                            inputClassName,
                        )}
                        name={name}
                        type={type}
                        value={value}
                        placeholder={type === 'date' && !placeholder ? 'yyyy-mm-dd' : placeholder}
                        onChange={onInput}
                        onKeyDown={onKeyDown}
                        defaultValue={defaultValue}
                        form={form}
                        size={size}
                        aria-label={label}
                        min={min}
                        readOnly={readonly}
                        max={max}
                        onPaste={onPaste}
                        onDrop={onDrop}
                        autoComplete={autoComplete}
                        {...(type === 'date' && { 'data-format': 'yyyy-MM-dd' })}
                    />

                    {suffix != null && (
                        <div
                            className={twMerge(
                                'flex flex-shrink-0 flex-col items-center justify-center px-3',
                                suffixClassName,
                            )}>
                            {suffix}
                        </div>
                    )}
                </div>

                {rightAddon != null && rightAddon}
            </div>

            {successMessage && <div className="text-sm text-green-600">{successMessage}</div>}
            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
            {!displayError && description != null && <div className="text-sm text-neutral-500">{description}</div>}
        </div>
    );
}
